package com.milta.feature.route.domain.entity

import kotlinx.serialization.Serializable

/**
 * The only two outcomes a driver can submit for an in-progress stop (MIL-41 contract). Distinct
 * from [StopStatus]/[DeliveryStatus] (which also cover `Pending`/`Untreated`, never valid here) —
 * the backend's `ValidateDeliveryHandler.ParseStatus` rejects anything but these two.
 *
 * `@Serializable` — persisted as part of [PendingDeliveryValidation] (mode hors ligne queue).
 */
@Serializable
enum class DeliveryOutcome { Completed, Failed }

/**
 * Result of `POST /routes/{routeId}/deliveries/{deliveryId}/validate` — the delivery as recorded
 * once the driver confirms/fails a stop with its mandatory proof photo (MIL-41, écran "Preuve de
 * livraison"). [recipientName] mirrors the optional field on `Delivery` (MIL-28).
 */
data class DeliveryProof(
    val id: String,
    val routeId: String,
    val status: DeliveryOutcome,
    val recipientName: String?,
    val address: String,
    val comment: String?,
    val proofPhotoUrl: String,
    /** ISO-8601 `DateTimeOffset`. */
    val completedAt: String,
)

/** Bug fix — lets the caller patch a just-validated [RouteStop] in place with the real outcome
 * instead of leaving it frozen at [StopStatus.Pending] in a list threaded across screens (see
 * MainActivity's `RouteFlowStep.DeliveryConfirmStep` handling: "next stop" used to keep resolving
 * to an already-validated stop because that list was never corrected after each hop). */
fun DeliveryOutcome.toStopStatus(): StopStatus = when (this) {
    DeliveryOutcome.Completed -> StopStatus.Completed
    DeliveryOutcome.Failed -> StopStatus.Failed
}
